// REQ-400: figure highlight (rectangle/ellipse) tool. Single source for the palette, the line-style
// catalogue, and every field's starting value — the tool-options row now exposes all five controls
// (#1381 review), so these are seeds for figureHighlight.store's live-editable state, not fixed
// request values.

/**
 * 17-swatch palette from Figma ("Highlight / Colors / Opened", node 18916:553505), 6-per-row.
 * Shared by both the stroke ("Color") and fill ("Fill color") pickers — the backend's `color` and
 * `fill_color` fields are independently settable (verified via `pnpm run api:show`), matching the
 * two separate buttons the fuller "Rectangle / Toolbox hover" mock shows (node 19019:672061).
 */
export const HIGHLIGHT_PALETTE: readonly string[] = [
	"#FF0000",
	"#FF9000",
	"#FFFF00",
	"#56B300",
	"#0096FF",
	"#FF00FF",
	"#EA5984",
	"#FF8351",
	"#FFEE67",
	"#6BDB61",
	"#81BAFF",
	"#FF7FFF",
	"#FFFFFF",
	"#C0C0C0",
	"#808080",
	"#404040",
	"#000000",
]

// No default swatch is indicated as "selected" in either linked Figma frame (only the freehand
// mock's unrelated screenshot shows a selection, on black) — picking the first (red) as the tool's
// starting color, for both stroke and fill, is a judgment call, easy to revisit here.
export const DEFAULT_HIGHLIGHT_STROKE_COLOR: string = HIGHLIGHT_PALETTE[0]
// `null` is a first-class value for the FILL only (#1386 review: "the fill must have a no-fill
// option - like on the design"): the backend's `fill_color` is `string | null` and null means a
// transparent fill (verified via `pnpm run api:show`), so the picker's extra slashed swatch maps
// straight onto it. The stroke has no such option — a highlight with no outline color makes no
// sense — which is why only the fill picker opts into it (`allowNoFill` on ColorSwatchPicker).
export const DEFAULT_HIGHLIGHT_FILL_COLOR: string | null = HIGHLIGHT_PALETTE[0]

// Starting opacity (%, matches the API's 0-100 range). The backend rendering-style decision (#2021)
// is still open; the live API example uses 44, and the Figma "Opacity / Opened" mock also shows 44%.
export const DEFAULT_HIGHLIGHT_OPACITY = 44
export const MIN_HIGHLIGHT_OPACITY = 0
export const MAX_HIGHLIGHT_OPACITY = 100

// Starting stroke width (pt) — matches the API's `stroke_width` bounds (1-20) and the Figma
// "Stroke / Opened" mock's own default of 2pt.
export const DEFAULT_HIGHLIGHT_STROKE_WIDTH = 2
export const MIN_HIGHLIGHT_STROKE_WIDTH = 1
export const MAX_HIGHLIGHT_STROKE_WIDTH = 20

/**
 * One row of the line-style dropdown. `preview` is the discriminator for HOW the row is drawn:
 * every catalogued style so far is a straight stroke described by an SVG `stroke-dasharray`, but
 * "cloudy" (#1386 review, flagged major — "users use it a lot in PDF files they provide") is a
 * scalloped border that no dash pattern can express, so it needs its own preview path rather than a
 * faked dasharray. Absent `preview` means "dash", keeping every existing row untouched.
 */
export type TLineStyleOption = {
	value: string
	dasharray: string
	preview?: "cloudy"
}

/**
 * The `line_style` value for a cloudy border. Named rather than inlined because three places have to
 * agree on it: this catalogue, the header (which hides the row for a shape that cannot render it) and
 * `useFigureHighlightCreate` (which resets it when such a shape is armed). `annotations.service.ts`
 * keeps its own copy on purpose — `services/` never imports from `components/` in this codebase.
 */
export const CLOUDY_LINE_STYLE = "cloudy"

/**
 * Line-style catalogue (Figma "Rectangle / Line style / Opened", node 19019:672610's linked design)
 * — 7 rows: solid, three dash densities, three dot densities, "solid" checked by default, plus
 * "cloudy" added in the #1386 review. The design shows only visual previews, no text labels or
 * backend values, so both `value` (sent as the
 * backend's free-form `line_style` string — verified unrestricted via `pnpm run api:show`) and the
 * dasharray used to preview each row are a judgment call, easy to revisit here. `dasharray` is in
 * SVG `stroke-dasharray` units for an 8pt-tall preview stroke; dotted rows pair a near-zero dash
 * length with `stroke-linecap: round` (set by the component) to render as dots, not short dashes.
 * "cloudy" carries no dasharray at all — it renders through Apryse's own `setBorderStyle("cloudy")`
 * border effect (see `annotations.service.ts`'s `HIGHLIGHT_CLOUDY_LINE_STYLE`), not through
 * `Style`/`Dashes`. It is also the one row that is not universally offered: this SDK build implements
 * that border effect for `RectangleAnnotation` but NOT for `EllipseAnnotation`, so the picker hides
 * the row for a shape whose support probe
 * (`AnnotationService.supportsCloudyBorder`) comes back false rather than letting the user save a
 * style that would silently draw solid.
 */
export const LINE_STYLE_OPTIONS: readonly TLineStyleOption[] = [
	{ value: "solid", dasharray: "" },
	{ value: "dashed_long", dasharray: "8 4" },
	{ value: "dashed_medium", dasharray: "5 3" },
	{ value: "dashed_short", dasharray: "3 2" },
	{ value: "dotted_large", dasharray: "0.5 4" },
	{ value: "dotted_medium", dasharray: "0.5 3" },
	{ value: "dotted_small", dasharray: "0.5 2" },
	{ value: CLOUDY_LINE_STYLE, dasharray: "", preview: "cloudy" },
]
export const DEFAULT_HIGHLIGHT_LINE_STYLE = LINE_STYLE_OPTIONS[0].value
